import { PreviewImageModal } from '@exam-center/components';
import {
  AddIcon,
  Box,
  CloseIcon,
  Flex,
  IconButton,
  Image,
  Loading,
  Text,
  useTheme,
} from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useController } from 'react-hook-form';

export interface ImageUploadResult {
  downloadSignedUrl: string;
  fileName: string;
}

export interface FormImagesInputProps {
  name: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  numberOfImages?: number;
  /**
   * Optional async upload handler. Should return image object keys.
   */
  uploadFiles?: (files: File[]) => Promise<string[]>;
  /**
   * Optional initial default (only used if not already provided in form's defaultValues).
   */
  defaultValue?: string[];
  /**
   * Optional get download signed URL handler.
   */
  getDownloadSignedUrls?: (fileNames: string[]) => Promise<string[]>;
}

const DEFAULT_MAX_IMAGES = 3;

export default function FormImagesInput(props: Readonly<FormImagesInputProps>) {
  const {
    name,
    label,
    required,
    disabled,
    numberOfImages = DEFAULT_MAX_IMAGES,
    uploadFiles,
    getDownloadSignedUrls,
    defaultValue,
  } = props;

  const defaultValueRef = useRef<string[] | undefined>(defaultValue);

  const {
    field: { ref, value, onChange },
    fieldState: { error },
  } = useController({
    name,
    defaultValue: defaultValueRef.current,
  });

  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [isPreviewModalOpen, { open: openPreviewModal, close: closePreviewModal }] =
    useDisclosure();

  const images = useMemo<string[]>(() => (Array.isArray(value) ? value : []), [value]);

  const [imageSignedUrls, setImageSignedUrls] = useState<string[]>([]);

  const getDownloadSignedUrlsRef = useRef(getDownloadSignedUrls);
  useEffect(() => {
    getDownloadSignedUrlsRef.current = getDownloadSignedUrls;
  }, [getDownloadSignedUrls]);

  useEffect(() => {
    let canceled = false;

    async function resolveUrls() {
      const resolver = getDownloadSignedUrlsRef.current;
      if (resolver) {
        try {
          const urls = await resolver(images);
          if (!canceled) {
            setImageSignedUrls((prev) => {
              const next = urls;
              if (prev.length === next.length && prev.every((v, i) => v === next[i])) return prev;
              return next;
            });
          }
        } catch {
          if (!canceled) {
            setImageSignedUrls((prev) => {
              const next = images;
              if (prev.length === next.length && prev.every((v, i) => v === next[i])) return prev;
              return next;
            });
          }
        }
      } else {
        setImageSignedUrls((prev) => {
          const next = images;
          if (prev.length === next.length && prev.every((v, i) => v === next[i])) return prev;
          return next;
        });
      }
    }

    resolveUrls();

    return () => {
      canceled = true;
    };
  }, [images]);

  const remainingSlots = Math.max(numberOfImages - imageSignedUrls.length, 0);

  const isError = !!error;
  const helperText = error?.message ?? '';

  function triggerFileDialog() {
    if (disabled || isUploading) return;
    fileInputRef.current?.click();
  }

  function readFilesAsDataUrls(files: File[]): Promise<string[]> {
    return Promise.all(
      files.map(
        (file) =>
          new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = (err) => reject(err);
            reader.readAsDataURL(file);
          }),
      ),
    );
  }

  const handleFilesSelected = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const list = event.target.files;
      if (!list || list.length === 0) {
        event.target.value = '';
        return;
      }

      try {
        const files = Array.from(list).slice(0, remainingSlots);
        if (files.length === 0) {
          event.target.value = '';
          return;
        }

        if (uploadFiles) {
          setIsUploading(true);
          const uploaded = await uploadFiles(files);
          const next = [...images, ...uploaded].slice(0, numberOfImages);
          onChange(next);
        } else {
          const dataUrls = await readFilesAsDataUrls(files);
          const next = [...images, ...dataUrls].slice(0, numberOfImages);
          onChange(next);
        }
      } finally {
        setIsUploading(false);
        event.target.value = '';
      }
    },
    [remainingSlots, uploadFiles, images, numberOfImages, onChange],
  );

  function handleRemoveAt(idx: number) {
    if (disabled || isUploading) return;
    const next = images.filter((_, i) => i !== idx);
    onChange(next);
  }

  return (
    <>
      <Flex
        sx={{
          width: '100%',
          flexDirection: 'column',
          gap: (theme) => theme.spacing(1),
        }}
      >
        {label ? (
          <Flex sx={{ justifyContent: 'space-between' }}>
            <Flex sx={{ gap: (theme) => theme.spacing(1) }}>
              <Text variant='body1'>{label}</Text>
              {required ? (
                <Text variant='body1' color='error'>
                  *
                </Text>
              ) : null}
            </Flex>
            <Text variant='body1'>
              จำนวนรูปที่อัปโหลด {imageSignedUrls.length}/{numberOfImages}
            </Text>
          </Flex>
        ) : null}

        <input
          ref={(e) => {
            fileInputRef.current = e;
            ref(e);
          }}
          type='file'
          accept='image/*'
          multiple={remainingSlots > 1}
          disabled={disabled || isUploading}
          style={{ display: 'none' }}
          onChange={handleFilesSelected}
        />

        <Flex
          sx={{
            flexWrap: 'wrap',
            gap: (theme) => theme.spacing(2),
          }}
        >
          {imageSignedUrls.map((src, idx) => (
            <Box
              key={idx}
              onClick={(e) => {
                e.stopPropagation();
                openPreviewModal();
                setPreviewImage(src);
              }}
              sx={{
                width: '200px',
                height: '200px',
                position: 'relative',
                borderRadius: theme.shape.rounded(2),
                cursor: disabled ? 'not-allowed' : 'pointer',
              }}
            >
              <Image
                src={src}
                alt={`Uploaded image ${idx + 1}`}
                style={{
                  width: '200px',
                  height: '200px',
                  display: 'block',
                  objectFit: 'cover',
                  borderRadius: theme.spacing(2),
                  filter: disabled ? 'grayscale(0.4) opacity(0.7)' : 'none',
                }}
              />
              {!disabled ? (
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveAt(idx);
                  }}
                  disabled={isUploading}
                  sx={(theme) => ({
                    p: theme.spacing(1),
                    backgroundColor: theme.palette.grey[100],
                    position: 'absolute',
                    right: '8px',
                    top: '8px',
                    '.MuiSvgIcon-root': {
                      fontSize: '20px',
                    },
                    opacity: isUploading ? 0.7 : 1,
                  })}
                >
                  <CloseIcon />
                </IconButton>
              ) : null}
            </Box>
          ))}

          {remainingSlots > 0
            ? Array.from({ length: remainingSlots }).map((_, slotIdx) => (
                <IconButton
                  key={`add-slot-${slotIdx}`}
                  sx={(theme) => ({
                    width: '200px',
                    height: '200px',
                    borderRadius: theme.shape.rounded(2),
                    backgroundColor: theme.palette.grey[50],
                    border: `1px dashed ${!isError ? theme.palette.grey[300] : theme.palette.error.main}`,
                    '.MuiSvgIcon-root': {
                      fontSize: '32px',
                    },
                    cursor: disabled || isUploading ? 'not-allowed' : 'pointer',
                    opacity: disabled ? 0.6 : isUploading ? 0.85 : 1,
                    position: 'relative',
                  })}
                  disableRipple
                  onClick={triggerFileDialog}
                  disabled={disabled || isUploading}
                >
                  {isUploading && slotIdx === 0 ? (
                    <Loading />
                  ) : (
                    <AddIcon sx={{ color: (theme) => theme.palette.grey[500] }} />
                  )}
                </IconButton>
              ))
            : null}
        </Flex>

        {isError ? (
          <Text variant='caption' color='error'>
            {helperText}
          </Text>
        ) : null}
      </Flex>
      <PreviewImageModal
        open={isPreviewModalOpen}
        image={previewImage ?? ''}
        onClose={closePreviewModal}
      />
    </>
  );
}
